/**
 * The sidebar's entries — read from the app's own manifest (`src/app/`, D118).
 *
 * There is no permissions endpoint behind this shell (the original shell fetched `/user/permission`):
 * what a visitor may reach is the registered module list, narrowed by `allowedItems`. The entries
 * come from the home tiles — one per destination — and every non-overlay module that has no tile
 * still gets a plain row, named after its path, so a module added without a tile is still navigable.
 * A module publishes its own sections once the module contract lands (F-08); this stays the fallback.
 *
 * It took a `ResolveTenantAppResponse` until D118 and now takes nothing: the manifest is a static
 * import, so there is no argument to thread and no null to guard.
 */
import { APP_HOME_TILES, APP_MODULES, UNLISTED_MODULE_PREFIXES } from "@/app";
import type { NavBarItem } from "@/app/types";
import { filterNavForTenant, routeUnderPrefix } from "./navFilter";

/** "/organisations" → "Organisations"; "/call-sheets" → "Call sheets". */
export function labelForPath(pathPrefix: string): string {
  const segment = pathPrefix.split("/").filter(Boolean).pop() ?? "";
  if (!segment) return pathPrefix;
  const words = segment.replace(/[-_]+/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A derived row is named after the MODULE, not its path. The path is sometimes a
 * deep-link entry point rather than a destination — the Passport's claim module is
 * mounted at `/l/:token`, which `labelForPath` turns into the meaningless row "L".
 * The import key always carries the module's real name (`@passport/claim` → "Claim"),
 * so prefer it and keep the path as the fallback for a module with no usable key.
 */
export function labelForModule(mfe: { importKey: string; pathPrefix: string }): string {
  const fromKey = mfe.importKey.split("/").filter(Boolean).pop() ?? "";
  return fromKey === "" ? labelForPath(mfe.pathPrefix) : labelForPath(`/${fromKey}`);
}

export function navForApp(tiles = APP_HOME_TILES, modules = APP_MODULES, unlisted: readonly string[] = UNLISTED_MODULE_PREFIXES): NavBarItem[] {
  // The tile's SCOPE and SECTION ride through untouched. They are the manifest's own statement of
  // which entries belong to one production and how the sidebar groups them; `nav/navSections.ts` is
  // the only thing that reads them. A tile that says nothing, and every derived row, is the APP's — a
  // module nobody scoped is not a production's just because the shell would like it to be.
  const fromTiles: NavBarItem[] = tiles.map((tile) => ({
    name: tile.title,
    route: tile.route,
    scope: tile.scope === "production" ? "production" : "app",
    section: tile.section ?? null,
    ...(tile.icon ? { icon: tile.icon } : {}),
    // A tile that names its own sections is a CONTEXT, not a destination (D51). Carried through
    // untouched, like scope and section: the shell reads them, it does not decide them.
    ...(tile.items && tile.items.length > 0 ? { items: tile.items } : {}),
    // D157: what a production entry needs of the caller's standing — read by `nav/navStanding.ts`.
    ...(tile.standing ? { standing: tile.standing } : {}),
  }));
  const derived: NavBarItem[] = modules
    // 🔴 D157's My production is a door into `/profile` offered only to somebody engaged on ONE
    // production with nothing else there. It must not stand in for the Profile module's own row on the
    // app rail — counting it would take Profile away from everybody.
    // 🔴 A module switched off by a build flag (PNK-246: Payments) is registered so its import map and
    // sitemap agree, but must not reappear as a derived "Payments" row with its tiles held back.
    .filter((mfe) => !unlisted.includes(mfe.pathPrefix))
    .filter(
      (mfe) =>
        !mfe.overlay && !fromTiles.some((item) => item.standing?.engagedOnly !== true && routeUnderPrefix(item.route, mfe.pathPrefix)),
    )
    .map((mfe) => ({
      name: labelForModule(mfe),
      route: mfe.pathPrefix,
      scope: "app" as const,
      section: null,
      icon: "apps",
    }));
  return filterNavForTenant([...fromTiles, ...derived], modules);
}
